import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import Navbar from '../Layout/Navbar';
import './AdherenceDashboard.css';

interface WeeklyAdherence {
  week_start: string;
  adherence_score: number;
  workouts_completed: number;
  workouts_planned: number;
  mileage_completed: number;
  mileage_planned: number;
  long_run_completed: boolean;
  quality_workout_completed: boolean;
  workout_adherence_pct: number;
  mileage_adherence_pct: number;
  rest_adherence_pct: number;
}

interface CumulativeMetrics {
  avg_weekly_adherence: number;
  weeks_tracked: number;
  consistency_streak: number;
  long_run_completion_rate: number;
  quality_workout_completion_rate: number;
}

interface Props {
  embedded?: boolean;
}

const AdherenceDashboard: React.FC<Props> = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState<WeeklyAdherence[]>([]);
  const [cumulative, setCumulative] = useState<CumulativeMetrics | null>(null);
  const [planInactive, setPlanInactive] = useState<{ status: string; race_name: string; race_date: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadAdherenceData();
    api.trackEvent('adherence_viewed');
  }, []);

  const loadAdherenceData = async () => {
    try {
      setLoading(true);
      const data = (await api.getAdherenceHistory(4)) as any;
      if (data && data.plan_active === false) {
        setPlanInactive({
          status: data.last_plan_status,
          race_name: data.last_race_name,
          race_date: data.last_race_date,
        });
        setHistory([]);
        setCumulative(null);
        return;
      }
      setPlanInactive(null);
      setHistory(data.history);
      setCumulative(data.cumulative);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score: number): string => {
    if (score >= 85) return '#28a745';
    if (score >= 70) return '#ffc107';
    if (score >= 50) return '#ff9800';
    return '#dc3545';
  };

  const getScoreLabel = (score: number): string => {
    if (score >= 85) return 'Excellent';
    if (score >= 70) return 'Good';
    if (score >= 50) return 'Fair';
    return 'Needs Work';
  };

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getMotivationalMessage = (week: WeeklyAdherence): {
    title: string;
    message: string;
    suggestions: string[];
  } | null => {
    const score = week.adherence_score;

    // Green zone - no message needed
    if (score >= 85) return null;

    // Yellow or red zone - show motivational breakdown
    const suggestions: string[] = [];

    // Analyze what's dragging the score down
    if (week.workout_adherence_pct < 80) {
      const remaining = week.workouts_planned - week.workouts_completed;
      suggestions.push(`Complete ${remaining} more workout${remaining > 1 ? 's' : ''} to boost your score`);
    }

    if (week.mileage_adherence_pct < 70) {
      const remaining = Math.ceil(week.mileage_planned - week.mileage_completed);
      suggestions.push(`${remaining} more miles gets you closer to your target`);
    }

    if (!week.long_run_completed) {
      suggestions.push('Prioritize your long run - it\'s critical for endurance');
    }

    if (!week.quality_workout_completed) {
      suggestions.push('Fit in a quality workout (tempo/intervals) to improve pace');
    }

    // Calculate potential score improvement
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const potential = Math.min(
      week.adherence_score + (100 - week.workout_adherence_pct) * 0.5,
      95
    );

    let title = '';
    let message = '';

    if (score < 70) {
      // Red zone
      title = '💪 You Can Turn This Around!';
      message = `Your workout quality is ${week.workout_adherence_pct.toFixed(0)}%, but you're at ${week.workouts_completed}/${week.workouts_planned} workouts. Small consistent steps add up!`;
    } else {
      // Yellow zone
      title = '🎯 You\'re Close!';
      message = `You're at ${score}% - just ${Math.ceil(85 - score)}% away from excellent. Here's how to get there:`;
    }

    return { title, message, suggestions };
  };

  if (loading) {
    if (embedded) return <div className="loading">Loading adherence data...</div>;
    return (
      <>
        <Navbar />
        <div className="loading">Loading adherence data...</div>
      </>
    );
  }

  if (error) {
    const inner = <div className="adherence-container"><div className="error-message">{error}</div></div>;
    if (embedded) return <div className="adherence-page">{inner}</div>;
    return <><Navbar /><div className="adherence-page">{inner}</div></>;
  }

  if (planInactive) {
    const inner = (
      <div className="adherence-container">
        {!embedded && <h1>Training Adherence</h1>}
        <div className="no-data">
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>
            {planInactive.status === 'completed' ? '🏁' : '⏰'}
          </div>
          <h2>
            {planInactive.status === 'completed'
              ? 'Training Plan Complete'
              : 'No Active Training Plan'}
          </h2>
          <p>
            Your last training plan was for <strong>{planInactive.race_name}</strong> on{' '}
            {new Date(planInactive.race_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}.
            Set a new race goal to start tracking adherence again.
          </p>
          {!embedded && (
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', marginTop: '1rem', flexWrap: 'wrap' }}>
              <button onClick={() => navigate('/race-goal')} className="btn-primary">
                Set a New Race Goal
              </button>
              <button onClick={() => navigate('/race-history')} className="btn-secondary">
                View Race History
              </button>
            </div>
          )}
        </div>
      </div>
    );
    if (embedded) return <div className="adherence-page">{inner}</div>;
    return <><Navbar /><div className="adherence-page">{inner}</div></>;
  }

  if (!cumulative || cumulative.weeks_tracked === 0) {
    const inner = (
      <div className="adherence-container">
        {!embedded && <h1>Training Adherence</h1>}
        <div className="no-data">
          <h2>No Adherence Data Yet</h2>
          <p>Generate a training plan and complete workouts to track your adherence.</p>
          {!embedded && (
            <button onClick={() => navigate('/training-plan')} className="btn-primary">
              Go to Training Plan
            </button>
          )}
        </div>
      </div>
    );
    if (embedded) return <div className="adherence-page">{inner}</div>;
    return <><Navbar /><div className="adherence-page">{inner}</div></>;
  }

  const content = (
    <div className="adherence-container">
      {!embedded && <h1>Training Adherence</h1>}
      {!embedded && <p className="subtitle">Track your consistency and goal probability</p>}

          {/* Cumulative Score Card */}
          <div className="cumulative-card">
            <div className="cumulative-header">
              <h2>Overall Performance</h2>
              <div className="weeks-tracked">{cumulative.weeks_tracked} weeks tracked</div>
            </div>

            <div className="cumulative-stats">
              <div className="stat-card main-score">
                <div className="stat-label">Average Adherence</div>
                <div
                  className="stat-value-large"
                  style={{ color: getScoreColor(cumulative.avg_weekly_adherence) }}
                >
                  {cumulative.avg_weekly_adherence}%
                </div>
                <div className="stat-sublabel">{getScoreLabel(cumulative.avg_weekly_adherence)}</div>
              </div>

              <div className="stat-card">
                <div className="stat-label">Consistency Streak</div>
                <div className="stat-value">
                  {cumulative.consistency_streak} {cumulative.consistency_streak === 1 ? 'week' : 'weeks'}
                </div>
                <div className="stat-sublabel">≥70% adherence</div>
              </div>

              <div className="stat-card">
                <div className="stat-label">Long Runs</div>
                <div className="stat-value">{cumulative.long_run_completion_rate}%</div>
                <div className="stat-sublabel">completion rate</div>
              </div>

              <div className="stat-card">
                <div className="stat-label">Quality Workouts</div>
                <div className="stat-value">{cumulative.quality_workout_completion_rate}%</div>
                <div className="stat-sublabel">completion rate</div>
              </div>
            </div>

            {/* Recommendations */}
            {cumulative.avg_weekly_adherence < 80 && (
              <div className="adherence-recommendations">
                <h3>💡 Tips to Improve</h3>
                <ul>
                  {cumulative.avg_weekly_adherence < 70 && (
                    <li>Aim for 80%+ weekly adherence to maximize your race success rate</li>
                  )}
                  {cumulative.long_run_completion_rate < 75 && (
                    <li>Prioritize long runs - they're critical for building endurance</li>
                  )}
                  {cumulative.quality_workout_completion_rate < 60 && (
                    <li>Complete quality workouts (tempo/intervals) to improve race pace</li>
                  )}
                  {cumulative.consistency_streak < 2 && (
                    <li>Focus on consistency - build a 4+ week streak of 70%+ adherence</li>
                  )}
                </ul>
              </div>
            )}

            {cumulative.avg_weekly_adherence >= 85 && (
              <div className="adherence-praise">
                <h3>🎉 Outstanding Work!</h3>
                <p>Your consistency is excellent. Keep it up and you're on track to crush your goal!</p>
              </div>
            )}
          </div>

          {/* Weekly Breakdown */}
          <div className="weekly-breakdown">
            <h2>Weekly Breakdown</h2>
            <div className="weeks-list">
              {history.map((week, idx) => (
                <div key={idx} className="week-card">
                  <div className="week-header">
                    <div className="week-date">
                      Week of {formatDate(week.week_start)}
                    </div>
                    <div
                      className="week-score"
                      style={{ color: getScoreColor(week.adherence_score) }}
                    >
                      {week.adherence_score}%
                    </div>
                  </div>

                  <div className="week-details">
                    <div className="detail-row">
                      <span className="detail-label">Workouts:</span>
                      <span className="detail-value">
                        {week.workouts_completed}/{week.workouts_planned} completed
                      </span>
                    </div>

                    <div className="detail-row">
                      <span className="detail-label">Mileage:</span>
                      <span className="detail-value">
                        {week.mileage_completed.toFixed(1)}/{week.mileage_planned.toFixed(1)} mi
                        <span className="percentage">
                          ({Math.round(week.mileage_completed / week.mileage_planned * 100)}%)
                        </span>
                      </span>
                    </div>

                    <div className="detail-row">
                      <span className="detail-label">Long Run:</span>
                      <span className={`status-badge ${week.long_run_completed ? 'completed' : 'missed'}`}>
                        {week.long_run_completed ? '✓ Completed' : '✗ Missed'}
                      </span>
                    </div>

                    <div className="detail-row">
                      <span className="detail-label">Quality Workout:</span>
                      <span className={`status-badge ${week.quality_workout_completed ? 'completed' : 'missed'}`}>
                        {week.quality_workout_completed ? '✓ Completed' : '✗ Missed'}
                      </span>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="progress-bar-container">
                    <div
                      className="progress-bar"
                      style={{
                        width: `${week.adherence_score}%`,
                        backgroundColor: getScoreColor(week.adherence_score)
                      }}
                    />
                  </div>

                  {/* Motivational Breakdown */}
                  {(() => {
                    const motivation = getMotivationalMessage(week);
                    if (!motivation) return null;

                    return (
                      <div className={`motivational-breakdown ${week.adherence_score < 70 ? 'red-zone' : 'yellow-zone'}`}>
                        <div className="motivation-header">
                          <strong>{motivation.title}</strong>
                        </div>
                        <p className="motivation-message">{motivation.message}</p>

                        {/* Score breakdown */}
                        <div className="score-breakdown">
                          <div className="breakdown-item">
                            <span className="breakdown-label">Workout Quality:</span>
                            <span className="breakdown-value" style={{ color: getScoreColor(week.workout_adherence_pct) }}>
                              {week.workout_adherence_pct.toFixed(0)}%
                            </span>
                          </div>
                          <div className="breakdown-item">
                            <span className="breakdown-label">Mileage Target:</span>
                            <span className="breakdown-value" style={{ color: getScoreColor(week.mileage_adherence_pct) }}>
                              {week.mileage_adherence_pct.toFixed(0)}%
                            </span>
                          </div>
                          <div className="breakdown-item">
                            <span className="breakdown-label">Rest Adherence:</span>
                            <span className="breakdown-value" style={{ color: getScoreColor(week.rest_adherence_pct) }}>
                              {week.rest_adherence_pct.toFixed(0)}%
                            </span>
                          </div>
                        </div>

                        {/* Actionable suggestions */}
                        {motivation.suggestions.length > 0 && (
                          <div className="suggestions">
                            <div className="suggestions-title">💡 How to Improve:</div>
                            <ul>
                              {motivation.suggestions.map((suggestion, i) => (
                                <li key={i}>{suggestion}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              ))}
            </div>
          </div>

          {/* Actions — only shown on standalone page */}
          {!embedded && (
            <div className="adherence-actions">
              <button onClick={() => navigate('/training-plan')} className="btn-primary">
                View Current Plan
              </button>
              <button onClick={() => navigate('/dashboard')} className="btn-secondary">
                Back to Dashboard
              </button>
            </div>
          )}
        </div>
  );

  if (embedded) return <div className="adherence-page">{content}</div>;
  return <><Navbar /><div className="adherence-page">{content}</div></>;
};

export default AdherenceDashboard;
